/**
 * Подання в панелі списку (FLT-02, 03, 06, 07; E4a-W3) — частина першого екрана: назва-кнопка
 * перемикача, позначка «• змінено», дії «Зберегти» / «Зберегти як нове» / «Скинути» і меню «⋯» дій з
 * поданням. Розставляє їх спільна панель `view-toolbar.tsx` (дії — у рядку з «⋯», аудит № 11). Усе важке — перемикач, діалоги, банер конфлікту й самі дії — у лінивому чанку
 * `views/ViewTools.tsx`: він вантажиться, щойно подання стало «зміненим», або на намір людини
 * (фокус, наведення, клік).
 *
 * Що таке «змінено», вирішує чернетка з адреси (`?d=`, view-draft.ts). Коли людина вперше щось
 * змінила, модуль запам'ятовує редакцію подання (`rememberBase`): з нею «Зберегти» йде з
 * `If-Match`, і чужа зміна між ними — банер конфлікту, а не тихе перезаписування.
 *
 * Фільтр (E4a-W4) підключається через ту саму чернетку: `ViewDraft.filter` уже рахується в
 * «змінено», «Зберегти» й «Зберегти як нове».
 */
import type { MetaEntity, MetaView } from '@clientline/contracts';
import { useQueryClient } from '@tanstack/react-query';
import {
  lazy,
  type ReactNode,
  type RefObject,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import type { ColumnSpec } from '../features/list/columns.ts';
import { list as tl } from '../i18n/uk/list.ts';
import { useCapabilities } from '../prefs/prefs.ts';
import { useOnline } from '../shell/online.ts';
import { MenuButton, type MenuItem } from '../ui/Menu.tsx';
import { toast } from '../ui/notify/store.ts';
import type { OverflowEntry } from '../ui/OverflowRow.tsx';
import { RetryBoundary } from '../ui/RetryBoundary.tsx';
import { isDirty, type ViewDraft } from './view-draft.ts';
import { useWidestLabel } from './view-toolbar.tsx';

/** Лінивий чанк подань. Імпорт повторюється на кожну спробу: відхилений браузер не кешує. */
export const loadViewTools = () => import('./views/ViewTools.tsx');
type ViewTools = Awaited<ReturnType<typeof loadViewTools>>;
type ViewContext = import('./views/ViewTools.tsx').ViewContext;
type ViewPanel = import('./views/ViewTools.tsx').ViewPanel;

const panelsChunk = () => lazy(loadViewTools);
/** Модуль, якщо вже прийшов: забути редакцію при «чистому» поданні — без зайвого імпорту. */
let loaded: ViewTools | null = null;

export interface ViewBarProps {
  view: MetaView;
  entity: MetaEntity;
  /** Подання сутності з `/meta`, разом з поточним. */
  views: readonly MetaView[];
  draft: ViewDraft;
  /**
   * Стовпці на екрані (з особистим шаром): «Зберегти як нове» бере їх. Немає — екран без таблиці
   * (дошка): нове подання бере стовпці збереженого.
   */
  columns?: readonly ColumnSpec[] | undefined;
  widths: Readonly<Record<string, number>> | undefined;
  permissions: readonly string[];
  /** Відкрити подання; з `draft` — разом із незбереженими змінами (`?d=`). */
  open: (viewKey: string, draft?: ViewDraft) => void;
}

export interface ViewBar {
  /** `<h1>` панелі: назва подання — кнопка перемикача; місце — під найдовшу назву подань сутності. */
  title: ReactNode;
  /** «• змінено»; `null` — подання як є. */
  mark: ReactNode;
  /**
   * Дії над змінами для рядка дій панелі (`ViewToolbar`): «Зберегти» — основна, «Зберегти як нове»
   * і «Скинути» йдуть у «⋯», коли не вміщуються. Порожньо — змін немає.
   */
  changeActions: OverflowEntry[];
  /** Меню «⋯» дій з поданням. */
  menu: ReactNode;
  /** Пункти того самого меню: коли кнопка не вміщується, вони — групою в «⋯» панелі. */
  menuItems: MenuItem[];
  /** Перемикач, діалоги, банер конфлікту — під панеллю. */
  panels: ReactNode;
}

export function useViewBar(props: ViewBarProps): ViewBar {
  const { view, draft } = props;
  const client = useQueryClient();
  const online = useOnline();
  const { lite } = useCapabilities();
  const [panel, setPanel] = useState<ViewPanel | null>(null);
  const [Panels, setPanels] = useState(panelsChunk);
  const [saving, setSaving] = useState(false);
  const anchor = useRef<HTMLButtonElement>(null);

  // Свіжий контекст на кожен рендер: дії читають його в момент виклику, а не з замикання.
  const ctx = useRef<ViewContext | null>(null);
  ctx.current = { client, ...props, onConflict: () => setPanel({ kind: 'conflict' }) };

  const withTools = useCallback(
    (fn: (tools: ViewTools, context: ViewContext) => unknown) =>
      loadViewTools().then(
        (m) => {
          loaded = m;
          return ctx.current ? fn(m, ctx.current) : undefined;
        },
        () => toast({ text: tl.viewToolsFailed, tone: 'alert' }),
      ),
    [],
  );
  const preload = useCallback(() => {
    loadViewTools()
      .then((m) => {
        loaded = m;
      })
      .catch(() => undefined);
  }, []);
  // Панель відкривається, коли модуль уже тут: збій мережі — тост, а не порожнє місце.
  const openPanel = useCallback(
    (next: ViewPanel) => {
      void withTools(() => setPanel(next));
    },
    [withTools],
  );

  const dirty = isDirty(draft);
  useEffect(() => {
    if (!dirty) {
      loaded?.forgetBase(view.id);
      return;
    }
    loadViewTools()
      .then((m) => {
        loaded = m;
        return m.rememberBase(client, view);
      })
      .catch(() => undefined);
  }, [dirty, client, view]);
  // Банер конфлікту стосується змін: без них (збережено, скинуто, інше подання) він зайвий.
  useEffect(() => {
    if (!dirty) setPanel((p) => (p?.kind === 'conflict' ? null : p));
  }, [dirty]);

  const switcherOpen = panel?.kind === 'switcher';
  const title = (
    <ViewTitle
      anchor={anchor}
      label={view.label.uk}
      labels={props.views.map((v) => v.label.uk)}
      open={switcherOpen}
      onToggle={() => (switcherOpen ? setPanel(null) : openPanel({ kind: 'switcher' }))}
      onPreload={preload}
      lite={lite}
    />
  );

  const canSave = Boolean(view.id && view.canEdit);
  const save = () => {
    if (!online || saving) return;
    setSaving(true);
    void withTools((m, c) => m.saveChanges(c)).finally(() => setSaving(false));
  };
  const mark = dirty ? (
    <div className="view-changes" data-testid="view-changes">
      <span className="view-changes__mark" data-testid="view-changed">
        {tl.viewChanged}
      </span>
    </div>
  ) : null;
  const saveAs = () => openPanel({ kind: 'name', mode: 'saveAs' });
  const reset = () => void withTools((m, c) => m.resetChanges(c));
  const changeActions: OverflowEntry[] = dirty
    ? [
        ...(canSave
          ? [
              {
                key: 'view-save',
                name: tl.viewSave,
                node: (
                  <button
                    className="btn btn--sm btn--primary"
                    type="button"
                    aria-disabled={!online || saving}
                    aria-busy={saving}
                    title={online ? undefined : tl.viewSaveOffline}
                    onClick={save}
                    data-testid="view-save"
                  >
                    {tl.viewSave}
                  </button>
                ),
                menu: [
                  { kind: 'item' as const, id: 'view-save', label: tl.viewSave, onSelect: save },
                ],
              },
            ]
          : []),
        {
          key: 'view-save-as',
          name: tl.viewSaveAs,
          node: (
            <button
              className={`btn btn--sm${canSave ? '' : ' btn--primary'}`}
              type="button"
              aria-haspopup="dialog"
              onClick={saveAs}
              data-testid="view-save-as"
            >
              {tl.viewSaveAs}
            </button>
          ),
          menu: [{ kind: 'item', id: 'view-save-as', label: tl.viewSaveAs, onSelect: saveAs }],
        },
        {
          key: 'view-reset',
          name: tl.viewReset,
          node: (
            <button
              className="btn btn--sm btn--ghost"
              type="button"
              onClick={reset}
              data-testid="view-reset"
            >
              {tl.viewReset}
            </button>
          ),
          menu: [{ kind: 'item', id: 'view-reset', label: tl.viewReset, onSelect: reset }],
        },
      ]
    : [];

  const items: MenuItem[] = [];
  if (view.id) {
    items.push({
      kind: 'item',
      id: 'pin',
      label: view.prefs?.pinned ? tl.viewUnpin : tl.viewPin,
      onSelect: () => void withTools((m, c) => m.togglePin(c.client, c.view)),
    });
  }
  if (view.id && view.own) {
    items.push({
      kind: 'item',
      id: 'rename',
      label: tl.viewRename,
      onSelect: () => openPanel({ kind: 'name', mode: 'rename' }),
    });
  }
  if (view.id) {
    items.push({
      kind: 'item',
      id: 'duplicate',
      label: tl.viewDuplicate,
      onSelect: () => openPanel({ kind: 'name', mode: 'duplicate' }),
    });
  }
  if (view.id && view.own) {
    items.push({
      kind: 'item',
      id: 'visibility',
      label: tl.viewVisibility,
      onSelect: () => openPanel({ kind: 'visibility' }),
    });
  }
  if (view.id) {
    items.push({
      kind: 'item',
      id: 'default',
      label: view.default ? tl.viewUnsetDefault : tl.viewMakeDefault,
      onSelect: () => void withTools((m, c) => m.toggleDefault(c)),
    });
  }
  items.push({
    kind: 'item',
    id: 'link',
    label: tl.viewCopyLink,
    onSelect: () => void withTools((m) => m.copyLink()),
  });
  if (view.id && view.own) {
    items.push(
      { kind: 'separator', id: 'sep-delete' },
      {
        kind: 'item',
        id: 'delete',
        label: tl.viewDelete,
        tone: 'danger',
        onSelect: () => void withTools((m, c) => m.removeView(c)),
      },
    );
  }
  const menu = (
    <MenuButton label={tl.viewActions} items={items} className="btn btn--ghost btn--icon">
      <i className="ic ic-more" aria-hidden="true" />
    </MenuButton>
  );

  const context = ctx.current;
  const panels =
    panel && context ? (
      <RetryBoundary onRetry={() => setPanels(panelsChunk)} fallback={() => null}>
        <Suspense fallback={null}>
          <Panels panel={panel} ctx={context} anchor={anchor} onPanel={setPanel} />
        </Suspense>
      </RetryBoundary>
    ) : null;

  return { title, mark, changeActions, menu, menuItems: items, panels };
}

/**
 * `<h1>` панелі: назва подання — кнопка перемикача. Місце — під найдовшу назву подань сутності: зміна
 * подання не зсуває чипи (аудит № 14). Окремий компонент: замір назви (`useWidestLabel`, після
 * шрифту) перерендерює лише заголовок, а не весь екран списку чи дошки.
 */
function ViewTitle(props: {
  anchor: RefObject<HTMLButtonElement | null>;
  label: string;
  labels: readonly string[];
  open: boolean;
  onToggle: () => void;
  onPreload: () => void;
  lite: boolean;
}) {
  const labelRef = useRef<HTMLSpanElement>(null);
  const widest = useWidestLabel(labelRef, props.labels);
  return (
    <h1 className="toolbar__title">
      <button
        ref={props.anchor}
        className="view-switch"
        type="button"
        aria-haspopup="dialog"
        aria-expanded={props.open}
        title={tl.viewSwitcherHint}
        onClick={props.onToggle}
        onFocus={props.onPreload}
        onPointerEnter={props.lite ? undefined : props.onPreload}
        data-testid="view-switch"
      >
        <span
          ref={labelRef}
          className="view-switch__label"
          style={widest ? { width: widest } : undefined}
        >
          {props.label}
        </span>
        <i className="ic ic-chevron-down" aria-hidden="true" />
      </button>
    </h1>
  );
}

/** Лише для тестів: забути завантажений модуль. */
export const resetViewBarForTests = () => {
  loaded = null;
};
